<template>
  <v-container fluid style="max-width: 1920px">
    <v-row class="mb-6" no-gutters>
      <h1 class="accent--text">{{ $t("dashboard") }}</h1>
      <v-spacer></v-spacer>
      <div class="mt-2">
        <import-modal :devices="devices" />
        <v-btn
          :disabled="devices.length === 0"
          class="mr-2"
          outlined
          small
          color="primary"
          @click="clearDevices()"
        >
          <v-icon small left>mdi-delete-circle-outline</v-icon
          >{{ $t("clearDevices") }}
        </v-btn>
      </div>
    </v-row>
    <v-row no-gutters>
      <v-spacer class="hidden-sm-and-down"></v-spacer>
      <p class="accent--text mr-2">
        {{ $t("currentHost") }}:
        <v-tooltip :disabled="!firebaseAuth.host" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              v-bind="attrs"
              v-on="on"
              @click="firebaseAuth.host ? copyText() : {}"
              label
              small
              outlined
              class="mt-n1"
              color="primary"
              >{{ firebaseAuth.host ? `${firebaseAuth.host}` : "None" }}</v-chip
            >
          </template>
          <span>{{ copytext }}</span>
        </v-tooltip>
      </p>
    </v-row>

    <div>
      <v-data-table
        :headers="headers"
        :items="devices"
        single-expand
        item-key="id"
        class="elevation-1"
        :mobile-breakpoint="0"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title class="accent--text">{{
              devices.length > 0
                ? $t("table.headerText")
                : $t("table.headerTextNull")
            }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn small outlined color="primary" @click="getDevices()">{{
              $t("refresh")
            }}</v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.lastUpdate="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-chip
                  :color="onlineColor(item.LastUpdateUnix, item.sleepSeconds)"
                  dark
                  small
                  label
                >
                  {{ onlineStatus(item.LastUpdateUnix, item.sleepSeconds) }}
                </v-chip></span
              >
            </template>
            <span>{{ computedTimeAgo(item.LastUpdateUnix) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.firmware.update="{ item }">
          <v-chip
            v-if="item.firmware"
            :color="statusColor(item.firmware.update)"
            small
            label
            dark
          >
            {{ statusText(item.firmware.update) }}
            <v-progress-circular
              v-if="statusDld(item.firmware.update)"
              :size="15"
              :width="2"
              color="white"
              indeterminate
              class="ml-2"
            ></v-progress-circular>
          </v-chip>
        </template>
        <template v-slot:item.items="{ item }">
          <v-chip
            v-if="item.WiFiRSSI"
            class="mr-4 primary--text"
            color="primary lighten-4"
            dark
            small
            label
          >
            <v-icon left small>mdi-wifi</v-icon>
            {{ item.WiFiRSSI }}
          </v-chip>
          <v-chip
            v-if="item.BatVoltage"
            class="mr-4 primary--text"
            color="primary lighten-4"
            dark
            small
            label
          >
            <v-icon left small>mdi-battery-charging</v-icon>
            {{ item.BatVoltage }}V
          </v-chip>
        </template>

        <template v-slot:item.edit="{ item }">
          <upload-modal :item="item" :auth="firebaseAuth" />
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
// import firebase from "firebase";
import { mapState } from "vuex";
import moment from "moment";
import {
  computedOnlineStatusColor,
  computedOnlineStatusText,
} from "@/components/atoms/OnlineStatus.js";
import {
  computedStatusText,
  computedStatusColor,
  computedStatusDld,
} from "@/components/atoms/DownloadStatus.js";
import ImportModal from "@/components/ImportModal";
import UploadModal from "@/components/UploadModal";

export default {
  components: {
    ImportModal,
    UploadModal,
  },
  data() {
    return {
      timer: "",
      copytext: this.$i18n.t("copy.clipboard"),
      headers: [
        { text: this.$i18n.t("table.deviceID"), value: "id" },
        { text: this.$i18n.t("table.status"), value: "lastUpdate" },
        { text: this.$i18n.t("table.fileName"), value: "firmware.fileName" },
        { text: this.$i18n.t("table.status"), value: "firmware.update" },
        {
          text: this.$i18n.t("table.fileUploaded"),
          value: "firmware.uploadDate",
        },
        {
          text: this.$i18n.t("table.deviceDownloaded"),
          value: "firmware.lastupdate",
        },
        { text: this.$i18n.t("table.info"), value: "items", width: "200" },
        { text: this.$i18n.t("table.upload"), value: "edit", align: "center" },
      ],
    };
  },
  computed: {
    ...mapState({
      auth: (state) => state.device.auth,
      uploading: (state) => state.device.uploading,
      firebaseAuth: (state) => state.device.firebaseAuth,
      devices: (state) => state.device.devices,
    }),
  },
  created() {
    this.timer = setInterval(this.getDevices, 4000);
    setTimeout(
      function() {
        clearInterval(this.timer);
        this.$store.commit("setRefresh", true);
      }.bind(this),
      1800000
    );
  },
  methods: {
    async getDevices() {
      if (this.auth) {
        const payload = {
          host: this.firebaseAuth.host,
          secret: this.firebaseAuth.secret,
        };

        this.$store.dispatch("fetchAllDevices", payload);

        console.log("Refreshed");
      } else {
        console.log("Not Authenticated");
        return;
      }
    },

    copyText() {
      navigator.clipboard.writeText(this.host);
      this.copytext = this.$i18n.t("copy.copied");
      setTimeout(
        function() {
          this.copytext = this.$i18n.t("copy.clipboard");
        }.bind(this),
        2000
      );
    },

    cancelAutoUpdate() {
      clearInterval(this.timer);
    },

    clearDevices() {
      this.$store.dispatch("clearDevices");
    },

    // deleteAllFiles() {
    //   var deleteUpload = firebase.functions().httpsCallable("deleteUpload");

    //   deleteUpload();
    // },

    computedTimeAgo(time) {
      return time ? moment.unix(time).fromNow() : "Unknown";
    },

    statusColor(update) {
      return computedStatusColor(update);
    },

    statusText(update) {
      return computedStatusText(update);
    },

    statusDld(update) {
      return computedStatusDld(update);
    },

    onlineStatus(update, sleep) {
      return computedOnlineStatusText(update, sleep);
    },

    onlineColor(update, sleep) {
      return computedOnlineStatusColor(update, sleep);
    },
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>
